import { useState } from "react";
import {
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  CircleDashed,
} from "lucide-react";

export const COMPLIANCE_TYPES = [
  { key: "background_check", label: "Background check" },
  { key: "vulnerable_sector_check", label: "Vulnerable sector check" },
  { key: "criminal_record", label: "Criminal record check" },
  { key: "work_eligibility", label: "Work eligibility" },
  { key: "education_verification", label: "Education verified" },
  { key: "references", label: "References completed" },
  { key: "tb_test", label: "TB test" },
  { key: "immunizations", label: "Immunization records" },
  { key: "vaccinations", label: "Vaccinations" },
  { key: "cpr", label: "CPR certification" },
  { key: "first_aid", label: "First Aid certification" },
  { key: "nursing_license", label: "Nursing license" },
  { key: "professional_license", label: "Professional license" },
  { key: "drivers_license", label: "Driver's license" },
  { key: "insurance", label: "Insurance on file" },
  { key: "other", label: "Other" },
];

export const STATUSES = [
  { key: "pending", label: "Pending", Icon: CircleDashed, color: "text-clinical-subtext", bg: "bg-clinical-bg" },
  { key: "submitted", label: "Submitted", Icon: Clock, color: "text-clinical-primary", bg: "bg-clinical-primary/10" },
  { key: "verified", label: "Verified", Icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-100" },
  { key: "rejected", label: "Rejected", Icon: XCircle, color: "text-destructive", bg: "bg-destructive/10" },
];

export function effectiveStatus(item) {
  if (item.status !== "verified") return item.status || "pending";
  if (!item.expires_on) return "verified";
  const exp = new Date(item.expires_on + "T00:00:00");
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "expiring_soon";
  return "verified";
}

const EFFECTIVE_META = {
  verified:      { label: "Verified",      Icon: CheckCircle2, color: "text-emerald-700",  bg: "bg-emerald-100" },
  expiring_soon: { label: "Expiring soon", Icon: AlertTriangle, color: "text-amber-700",   bg: "bg-amber-100" },
  expired:       { label: "Expired",       Icon: AlertTriangle, color: "text-destructive", bg: "bg-destructive/10" },
  submitted:     { label: "Submitted",     Icon: Clock,         color: "text-clinical-primary", bg: "bg-clinical-primary/10" },
  pending:       { label: "Pending",       Icon: CircleDashed,  color: "text-clinical-subtext", bg: "bg-clinical-bg" },
  rejected:      { label: "Rejected",      Icon: XCircle,       color: "text-destructive", bg: "bg-destructive/10" },
};

export function StatusBadge({ status, daysLeft, testid }) {
  const meta = EFFECTIVE_META[status] || EFFECTIVE_META.pending;
  const Icon = meta.Icon;
  let extra = "";
  if (status === "expired" && daysLeft != null) extra = ` · ${Math.abs(daysLeft)}d ago`;
  if (status === "expiring_soon" && daysLeft != null) extra = ` · ${daysLeft}d left`;
  return (
    <span
      className={`pill ${meta.bg} ${meta.color} uppercase text-[10px] font-medium`}
      data-testid={testid}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
      {extra}
    </span>
  );
}

function newItem() {
  return {
    _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    type: "background_check",
    label: "",
    status: "pending",
    issued_on: "",
    expires_on: "",
    reference: "",
    note: "",
  };
}

export default function ComplianceEditor({ items, onChange, testidPrefix = "compliance" }) {
  const [openIdx, setOpenIdx] = useState(null);

  const add = () => {
    const next = [...(items || []), newItem()];
    onChange(next);
    setOpenIdx(next.length - 1);
  };
  const update = (idx, patch) =>
    onChange(items.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const remove = (idx) => {
    onChange(items.filter((_, i) => i !== idx));
    if (openIdx === idx) setOpenIdx(null);
  };

  return (
    <div className="space-y-2" data-testid={testidPrefix}>
      {(items || []).length === 0 && (
        <p className="text-xs text-clinical-subtext italic">
          No compliance items yet. Add background check, CPR, TB test, license, insurance, etc.
        </p>
      )}
      {(items || []).map((c, idx) => {
        const eff = effectiveStatus(c);
        const typeLabel =
          (COMPLIANCE_TYPES.find((t) => t.key === c.type) || { label: c.type }).label;
        const daysLeft = c.expires_on
          ? Math.ceil((new Date(c.expires_on + "T00:00:00").getTime() - Date.now()) / 86400000)
          : null;
        const isOpen = openIdx === idx;
        const stableKey = c.id || c._key || `${c.type}-${idx}`;
        return (
          <div
            key={stableKey}
            className="rounded-lg border border-clinical-border bg-white"
            data-testid={`${testidPrefix}-row-${idx}`}
          >
            <button
              type="button"
              onClick={() => setOpenIdx(isOpen ? null : idx)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-clinical-bg/40 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-clinical-text truncate">
                  {c.label || typeLabel}
                </div>
                <div className="text-[11px] text-clinical-subtext truncate">
                  {c.reference || (c.expires_on ? `Expires ${c.expires_on}` : "—")}
                </div>
              </div>
              <StatusBadge
                status={eff}
                daysLeft={daysLeft}
                testid={`${testidPrefix}-status-${idx}`}
              />
              <span className="ml-2 text-clinical-subtext text-xs">
                {isOpen ? "Hide" : "Edit"}
              </span>
            </button>
            {isOpen && (
              <div className="border-t border-clinical-border p-3 grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-5">
                  <label className="text-xs text-clinical-subtext block mb-1">Type</label>
                  <select
                    value={c.type}
                    onChange={(e) => update(idx, { type: e.target.value })}
                    className="input-clinical"
                    data-testid={`${testidPrefix}-type-${idx}`}
                  >
                    {COMPLIANCE_TYPES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-4">
                  <label className="text-xs text-clinical-subtext block mb-1">Status</label>
                  <select
                    value={c.status}
                    onChange={(e) => update(idx, { status: e.target.value })}
                    className="input-clinical"
                    data-testid={`${testidPrefix}-statusSelect-${idx}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-3 flex items-end">
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    className="btn-ghost hover:!text-destructive ml-auto"
                    data-testid={`${testidPrefix}-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" /> Remove
                  </button>
                </div>
                <div className="sm:col-span-6">
                  <label className="text-xs text-clinical-subtext block mb-1">Issued</label>
                  <input
                    type="date"
                    value={c.issued_on || ""}
                    onChange={(e) => update(idx, { issued_on: e.target.value })}
                    className="input-clinical"
                    data-testid={`${testidPrefix}-issued-${idx}`}
                  />
                </div>
                <div className="sm:col-span-6">
                  <label className="text-xs text-clinical-subtext block mb-1">Expires</label>
                  <input
                    type="date"
                    value={c.expires_on || ""}
                    onChange={(e) => update(idx, { expires_on: e.target.value })}
                    className="input-clinical"
                    data-testid={`${testidPrefix}-expires-${idx}`}
                  />
                </div>
                <div className="sm:col-span-12">
                  <label className="text-xs text-clinical-subtext block mb-1">Reference / vendor / file #</label>
                  <input
                    value={c.reference || ""}
                    onChange={(e) => update(idx, { reference: e.target.value })}
                    placeholder="e.g. Sterling order #ABC123"
                    className="input-clinical"
                    data-testid={`${testidPrefix}-reference-${idx}`}
                  />
                </div>
                <div className="sm:col-span-12">
                  <label className="text-xs text-clinical-subtext block mb-1">Admin note (not shown to family)</label>
                  <textarea
                    value={c.note || ""}
                    onChange={(e) => update(idx, { note: e.target.value })}
                    rows={2}
                    className="input-clinical resize-y"
                    data-testid={`${testidPrefix}-note-${idx}`}
                  />
                </div>
                {c.type === "other" && (
                  <div className="sm:col-span-12">
                    <label className="text-xs text-clinical-subtext block mb-1">
                      Custom label (for "Other")
                    </label>
                    <input
                      value={c.label || ""}
                      onChange={(e) => update(idx, { label: e.target.value })}
                      placeholder="e.g. Diabetes care certification"
                      className="input-clinical"
                      data-testid={`${testidPrefix}-label-${idx}`}
                    />
                  </div>
                )}
                {(c.status === "verified" && c.verified_by) && (
                  <div className="sm:col-span-12 text-[11px] text-clinical-subtext">
                    Verified by <span className="font-medium text-clinical-text">{c.verified_by}</span>
                    {c.verified_at && <> · {new Date(c.verified_at).toLocaleDateString()}</>}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        className="btn-ghost text-clinical-primary"
        data-testid={`${testidPrefix}-add`}
      >
        <Plus className="h-4 w-4" /> Add compliance item
      </button>
    </div>
  );
}
